/* @flow */

import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import _ from 'lodash';
import { Card, Row, Col } from 'reactstrap';
import cx from 'classnames';

import FaQuestionCircle from 'react-icons/lib/fa/question-circle';

import HoverTooltip from '../HoverTooltip';

import { createHTMLMarkup, branch } from '../../utils';
import { getFieldValues, getChangedFields, getFieldErrors } from '../../selectors/design';
import FieldTypeInput from '../FieldTypeInput';
import FieldRadioGroup from '../FieldRadioGroup';
import TableSelectionGroup from '../TableSelectionGroup';
import DropdownWithPanelGroup from '../DropdownWithPanel';

import classes from './FieldWrapper.scss';

type PropsType = {
  changed: boolean,
  currentValue: number | string,
  data: StepFieldData,
  defaultValue: number | string,
  error: string[],
  id: string,
  onChangeHandler: (id: string) => any,
  onRevertHandler: (id: string) => void,
  options: any,
  rangeMax: number,
  rangeMin: number,
};

export class FieldWrapper extends React.PureComponent<PropsType, *> {
  static defaultProps = {
    error: [],
    data: {
      type: '',
      step: '',
    },
    onChangeHandler: () => {},
    onRevertHandler: () => {},
  };

  onChange: Function;
  onRevert: Function;
  props: PropsType;

  constructor(props: PropsType): void { // eslint-disable-line react/sort-comp
    super(props);
    this.onChange = props.onChangeHandler(props.id);
    this.onRevert = (): void => props.onRevertHandler(props.id);
  }

  render() {
    const { currentValue, defaultValue, changed, rangeMin, rangeMax, error } = this.props;
    const data = this.props.data;

    const type = data.type ? data.type : 'text';
    const step = data.step ? data.step : '1';

    const isLoading = false;
    const isModified = changed && !isLoading;
    const isTable = type === 'table';
    const hasError = error.length > 0;

    const fieldName = <strong>{createHTMLMarkup(data.name)}</strong>;

    const tooltipDiv = (
      <HoverTooltip id={this.props.id} title={fieldName} text={createHTMLMarkup(data.tooltip)}>
        <FaQuestionCircle className={classes.infoIcon} size={24} />
      </HoverTooltip>
    );

    const tooltip = branch(_.isString(data.tooltip), tooltipDiv, null);

    const leftCol = (
      <Col sm={6} className="d-flex">
        {fieldName}
        {tooltip}
      </Col>
    );

    const cardClasses = !isTable ? {
      [classes.calculated]: data.calculated,
      [classes.error]: hasError,
      [classes.success]: isModified && !isLoading,
    } : {
      [classes.error]: hasError,
    };

    const hasLeftCol = !isTable && type !== 'dropdown';

    return (
      <Card className={cx(classes.card, cardClasses)}>
        <Row>
          { branch(hasLeftCol, leftCol, null) }
          {
            branch(
              type === 'number' || type === 'text',
              <FieldTypeInput
                value={currentValue}
                defaultVal={defaultValue}
                changed={changed}
                error={error}
                range={[rangeMin, rangeMax]}
                calculated={data.calculated}
                unit={data.unit}
                step={step}
                revertInput={this.onRevert}
                changeInput={this.onChange}
                type={type}
              />,
              null,
            )
          }
          {
            branch(
              type === 'radio',
              <FieldRadioGroup
                value={currentValue}
                defaultVal={defaultValue}
                changed={changed}
                error={error}
                revertInput={this.onRevert}
                changeInput={this.onChange}
                options={this.props.options}
              />,
              null,
            )
          }
          {
            branch(
              type === 'dropdown',
              <DropdownWithPanelGroup
                value={currentValue}
                defaultVal={defaultValue}
                changed={changed}
                error={error}
                revertInput={this.onRevert}
                changeInput={this.onChange}
                options={this.props.options}
                fieldName={fieldName}
                tooltip={tooltip}
              />,
              null,
            )
          }
          {
            branch(
              isTable,
              <TableSelectionGroup
                id={this.props.id}
                value={currentValue}
                defaultVal={defaultValue}
                changed={changed}
                error={error}
                dataTarget={data.data}
                revertInput={this.onRevert}
                changeInput={this.onChange}
                options={data.options}
              />,
            )
          }

        </Row>
      </Card>
    );
  }
}

function mapStateToProps(state: any, ownProps: PropsType) {
  const values = getFieldValues(state);
  const changedValues = getChangedFields(state);
  const fieldErrors = getFieldErrors(state);

  const data = values[ownProps.id];
  const changed = Boolean(changedValues[ownProps.id]);
  const error: string[] = fieldErrors[ownProps.id];

  // catch any radio buttons depending on other lists
  // TODO: hacky - should prob fix this at some point? maybe in reducer?
  let options = ownProps.data.options;
  if (ownProps.data.dataTarget) {
    options = _.get(values[ownProps.data.dataTarget], ['currentValue'], []);
  }

  return {
    rangeMin: _.get(data, ['rangeValue', 'min']),
    rangeMax: _.get(data, ['rangeValue', 'max']),
    options,
    changed,
    error,
    currentValue: _.get(data, ['currentValue']),
    defaultValue: _.get(data, ['defaultValue']),
  };
}

const enhancer = compose(
  connect(mapStateToProps),
);

export default enhancer(FieldWrapper);



// WEBPACK FOOTER //
// ./src/components/FieldWrapper/FieldWrapper.js